'use strict';

const P = require('./protocol.js'); // One owner-scoped session. Allocation and issuance exist exclusively on the server.


function createPackSession({
  api,
  packId,
  scopeKey,
  storage,
  makeRequestId,
  onChange = () => {}
}) {
  P.check(api && P.id(packId) && P.id(scopeKey) && storage && typeof makeRequestId === 'function', 'PACK_SESSION_CONFIG_REQUIRED');
  let current = null,
      busy = false,
      uncertain = false,
      disposed = false,
      version = 0,
      error = '';
  const key = 'op-card-open-v1:' + scopeKey + ':' + packId;

  function snapshot() {
    return Object.freeze({
      pack: current,
      busy,
      uncertain,
      error
    });
  }

  function emit() {
    if (!disposed) onChange(snapshot());
  }

  function accept(value) {
    const next = P.pack(value, packId);

    if (current) {
      var _next$grant;

      const rank = {
        SEALED: 0,
        REVEALED: 1,
        OPENED: 2
      };
      P.check(rank[next.status] >= rank[current.status], 'PACK_STATUS_REGRESSION');
      if (current.outcome) P.check(P.canonical(current.outcome) === P.canonical(next.outcome), 'PACK_OUTCOME_CHANGED');
      if (current.grant) P.check(current.grant.grantId === ((_next$grant = next.grant) === null || _next$grant === void 0 ? void 0 : _next$grant.grantId), 'PACK_GRANT_CHANGED');
    }

    current = next;
    return next;
  }

  function requestId(kind) {
    const k = key + ':' + kind;
    let value = storage.getItem(k);

    if (!value) {
      value = makeRequestId();
      P.check(P.id(value), 'INVALID_REQUEST_ID');
      storage.setItem(k, value);
      P.check(storage.getItem(k) === value, 'REQUEST_ID_NOT_PERSISTED');
    }

    P.check(P.id(value), 'INVALID_SAVED_REQUEST_ID');
    return value;
  }

  async function perform(task, write) {
    P.check(!disposed, 'PACK_SESSION_DISPOSED');
    if (busy) return snapshot();
    busy = true;
    error = '';
    const mine = ++version;
    emit();

    try {
      const result = await task();
      if (disposed || mine !== version) return snapshot();
      accept(result);
      uncertain = false;
    } catch (e) {
      if (!disposed && mine === version) {
        error = (e === null || e === void 0 ? void 0 : e.message) || 'PACK_REQUEST_FAILED';
        if (write) uncertain = true;
      }
    } finally {
      if (!disposed && mine === version) {
        busy = false;
        emit();
      }
    }

    return snapshot();
  }

  return Object.freeze({
    snapshot,
    load: () => perform(() => api.pack(packId), false),
    beginReveal: () => {
      var _current, _current2, _current3;

      if (((_current = current) === null || _current === void 0 ? void 0 : _current.status) === 'REVEALED' || ((_current2 = current) === null || _current2 === void 0 ? void 0 : _current2.status) === 'OPENED') return Promise.resolve(snapshot());
      P.check(((_current3 = current) === null || _current3 === void 0 ? void 0 : _current3.status) === 'SEALED', 'PACK_NOT_READY');
      return perform(() => api.reveal(packId, requestId('reveal')), true);
    },
    open: () => {
      var _current4, _current5;

      if (((_current4 = current) === null || _current4 === void 0 ? void 0 : _current4.status) === 'OPENED') return Promise.resolve(snapshot());
      P.check(((_current5 = current) === null || _current5 === void 0 ? void 0 : _current5.status) === 'REVEALED', 'PACK_NOT_REVEALED');
      return perform(() => api.open(packId, requestId('open')), true);
    },
    // Query after an unknown network outcome; explicit open retry reuses the saved request ID.
    reconcile: () => perform(() => api.pack(packId), false),

    dispose() {
      disposed = true;
      ++version;
      busy = false;
    }

  });
}

module.exports = {
  createPackSession
};
